'use client';

/**
 * Conveyor approval queue — popup stack (bottom-right) of pending task
 * proposals from the gateway conveyor (docs/CONVEYOR-PLAN.md).
 * Polls /api/gw/conveyor/proposals every 8s. Approve / Edit / Reject.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDragResize } from './useDragResize';

interface Proposal {
  id: string;
  slug: string;
  projectName: string;
  task: string;
  why: string;
  plain?: string;
  qa?: Array<{ q: string; a: string; at: number }>;
  mode?: string;
  est?: string;
  status: string;
  createdAt: number;
}

export default function ConveyorQueue() {
  const [pending, setPending] = useState<Proposal[]>([]);
  const [editing, setEditing] = useState<string | null>(null); // proposal id
  const [editText, setEditText] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);       // Ask box open
  // Tucked to a corner badge. On a phone the 300px panel is fixed over the
  // bottom third of the screen — exactly where a page's own action buttons
  // live (the Factory seeds ask's "Continue" sat under it; Boss: "no way to
  // approve the seeds on mobile", 2026-09-13). Start collapsed under 768px;
  // one tap opens it. Desktop keeps the open panel.
  const [collapsed, setCollapsed] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.innerWidth < 768);
  const [question, setQuestion] = useState('');
  const [waiting, setWaiting] = useState(false);     // answer in flight
  const [askErr, setAskErr] = useState<string | null>(null);
  // Show the technical task text only on demand — the plain-English version is
  // what Boss decides on.
  const [showTech, setShowTech] = useState(false);
  // Movable/resizable: the popup used to be pinned bottom-right over the chat
  // with no way to shift it aside, and long task descriptions had nowhere to go.
  const { style, dragHandleProps, resizeHandleProps, reset } = useDragResize({
    key: 'queue', defaultW: 420, defaultH: 300, anchor: 'bottom-right', minW: 320, minH: 180,
  });
  // The collapsed pill is draggable too: pinned bottom-right it sat exactly on
  // top of the chat composer's attach + send buttons (Boss, 2026-09-09). Only
  // x/y are used — the pill sizes itself. A drag must not count as a click.
  const pill = useDragResize({
    key: 'pill', defaultW: 170, defaultH: 36, anchor: 'bottom-right', minW: 40, minH: 30,
  });
  const pillDown = useRef<{ x: number; y: number } | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/gw/conveyor/proposals?status=pending');
      if (!res.ok) return;
      const data = await res.json();
      if (data?.ok) setPending(data.proposals as Proposal[]);
    } catch { /* gateway down — keep last state */ }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 8_000);
    return () => clearInterval(t);
  }, [refresh]);

  const ask = async (id: string, q: string) => {
    if (!q.trim()) return;
    setWaiting(true); setAskErr(null);
    try {
      // No timeout on our side: the session has to think, and may be queued
      // behind other runs. The gateway caps it at 180s and returns an error.
      const res = await fetch('/api/gw/conveyor/ask', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, question: q }),
      });
      const data = await res.json();
      if (!data?.ok) setAskErr(data?.error || 'the session could not answer');
      else setQuestion('');
    } catch { setAskErr('gateway unreachable'); }
    setWaiting(false);
    refresh();   // the answer is stored on the proposal
  };

  const decide = async (id: string, action: 'approve' | 'reject' | 'skip', editedText?: string) => {
    setBusy(id);
    try {
      await fetch(`/api/gw/conveyor/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, via: 'ui', ...(editedText ? { editedText } : {}) }),
      });
    } catch { /* surfaced by refresh */ }
    setBusy(null);
    setEditing(null);
    setEditText('');
    setAsking(false); setQuestion(''); setAskErr(null); setShowTech(false);
    refresh();
  };

  if (pending.length === 0) return null;

  // Collapsed: a corner badge instead of a panel over Boss's work.
  // The panel only appears when a decision IS pending, which is legitimate — but
  // "legitimate" is not the same as "must be dealt with this second". Without a
  // way to tuck it away, the only escapes were Approve / Reject / Skip, so
  // "get it off my screen" and "Skip" became the same gesture. 59 of 109
  // proposals are skipped. (Boss: "conveyor is very invasive", 2026-08-20.)
  if (collapsed) {
    if (!pill.box) return null;
    return (
      <button
        onPointerDown={(e) => {
          pillDown.current = pill.box ? { x: pill.box.x, y: pill.box.y } : null;
          pill.dragHandleProps.onPointerDown(e);
        }}
        onClick={() => {
          // Expand only if the pill didn't travel — a drag ends with a click too.
          const d = pillDown.current, b = pill.box;
          const moved = d && b && Math.hypot(b.x - d.x, b.y - d.y) > 4;
          if (!moved) setCollapsed(false);
        }}
        title="Conveyor — task awaiting your decision (drag to move)"
        className="fixed z-[100] flex items-center gap-2 rounded-full bg-zinc-800/90 hover:bg-zinc-700 border border-amber-500/50 px-3 py-2 shadow-lg select-none"
        style={{ left: pill.box.x, top: pill.box.y, cursor: 'grab', touchAction: 'none' }}
      >
        <span>🏭</span>
        <span className="text-xs text-amber-300">
          {pending.length} awaiting approval
        </span>
      </button>
    );
  }

  // `style` is undefined until the geometry effect has measured the viewport.
  if (!style) return null;
  const top = pending[0];
  const isEditing = editing === top.id;

  return (
    <div data-conveyor-panel="queue" className="fixed z-[100] flex flex-col rounded-xl border border-amber-500/40 bg-zinc-900/95 backdrop-blur shadow-2xl text-sm text-zinc-100 overflow-hidden" style={style}>
      <div className="flex flex-col flex-1 min-h-0 p-4">
        <div data-conveyor-drag className="flex items-center justify-between mb-2 select-none" {...dragHandleProps}>
          <div className="font-semibold text-amber-400">
            🏭 Conveyor — task approval
            {pending.length > 1 && (
              <span className="ml-2 rounded-full bg-amber-500/20 px-2 py-0.5 text-xs text-amber-300">
                +{pending.length - 1} queued
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-zinc-400">
            <span>{top.mode || ''}{top.est ? ` · est ${top.est}` : ''}</span>
            <button
              data-no-drag
              onClick={reset}
              title="Reset position and size"
              className="rounded px-1.5 py-0.5 text-zinc-500 hover:text-zinc-200 hover:bg-zinc-700/60"
            >
              ↺
            </button>
            <button
              data-no-drag
              onClick={() => setCollapsed(true)}
              title="Tuck away — keeps the task pending, decides nothing"
              className="rounded px-1.5 py-0.5 text-zinc-500 hover:text-zinc-200 hover:bg-zinc-700/60"
            >
              –
            </button>
          </div>
        </div>
        <div className="mb-1 text-xs uppercase tracking-wide text-zinc-400">{top.projectName} <span className="text-zinc-500">({top.slug})</span></div>
        {/* min-h-0 + overflow-y-auto: the body takes the slack when Boss makes
            the panel taller, and scrolls instead of pushing the buttons out. */}
        {isEditing ? (
          <textarea
            className="w-full flex-1 min-h-[7rem] rounded-md bg-zinc-800 border border-zinc-700 p-2 text-sm text-zinc-100 focus:outline-none focus:border-amber-500 resize-none"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            autoFocus
          />
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto">
            {/* PLAIN ENGLISH FIRST. The raw task is factory jargon ("iOS build
                carrying a pass") and is not a basis for a decision. */}
            {top.plain ? (
              <>
                <div className="mb-2 whitespace-pre-wrap">{top.plain}</div>
                <button
                  onClick={() => setShowTech((v) => !v)}
                  className="mb-2 text-xs text-zinc-500 hover:text-zinc-300 underline decoration-dotted"
                >
                  {showTech ? 'Hide the technical version' : 'Show the technical version'}
                </button>
                {showTech && (
                  <div className="mb-2 rounded-md bg-zinc-800/60 border border-zinc-700 p-2 text-xs text-zinc-300 whitespace-pre-wrap">
                    {top.task}
                    {top.why && <div className="mt-1 text-zinc-500 italic">Why: {top.why}</div>}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="mb-2 whitespace-pre-wrap">{top.task}</div>
                {top.why && <div className="mb-2 text-xs text-zinc-400 italic">Why: {top.why}</div>}
                <div className="mb-2 text-xs text-amber-500/80">
                  No plain-English summary on this one — use “Ask” below.
                </div>
              </>
            )}

            {/* Answers to earlier questions, kept with the proposal. */}
            {(top.qa || []).map((x, i) => (
              <div key={i} className="mt-2 rounded-md border border-sky-500/30 bg-sky-500/5 p-2">
                <div className="text-xs font-medium text-sky-300">❓ {x.q}</div>
                <div className="mt-1 whitespace-pre-wrap text-xs text-zinc-200">{x.a}</div>
              </div>
            ))}

            {asking && (
              <div className="mt-3">
                <textarea
                  className="w-full h-20 rounded-md bg-zinc-800 border border-zinc-700 p-2 text-sm text-zinc-100 focus:outline-none focus:border-sky-500 resize-none"
                  placeholder="What is this for? Why now? What breaks if we skip it?"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) ask(top.id, question);
                  }}
                  disabled={waiting}
                  autoFocus
                />
                <div className="mt-1 flex items-center gap-2">
                  <button
                    disabled={waiting || !question.trim()}
                    onClick={() => ask(top.id, question)}
                    className="rounded-md bg-sky-600 hover:bg-sky-500 disabled:opacity-50 px-3 py-1 text-xs font-medium"
                  >
                    {waiting ? 'Asking the project…' : 'Send question'}
                  </button>
                  <button
                    onClick={() => { setAsking(false); setQuestion(''); setAskErr(null); }}
                    className="rounded-md bg-zinc-700 hover:bg-zinc-600 px-3 py-1 text-xs"
                  >
                    Close
                  </button>
                  <span className="text-[10px] text-zinc-500">⌘↵ to send</span>
                </div>
                {waiting && (
                  <div className="mt-1 text-[11px] text-zinc-500">
                    The project session is reading its own files to answer — this can take a minute.
                  </div>
                )}
                {askErr && <div className="mt-1 text-[11px] text-red-400">{askErr}</div>}
              </div>
            )}
          </div>
        )}
        {/* flex-wrap: five buttons don't fit the 420px default width in one row */}
        <div className="mt-3 flex flex-wrap gap-2 shrink-0">
          {isEditing ? (
            <>
              <button
                disabled={busy === top.id || !editText.trim()}
                onClick={() => decide(top.id, 'approve', editText)}
                className="flex-1 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 px-3 py-1.5 font-medium"
              >
                ✅ Approve edited
              </button>
              <button
                onClick={() => { setEditing(null); setEditText(''); }}
                className="rounded-md bg-zinc-700 hover:bg-zinc-600 px-3 py-1.5"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                disabled={busy === top.id}
                onClick={() => decide(top.id, 'approve')}
                className="flex-1 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 px-3 py-1.5 font-medium"
              >
                ✅ Approve
              </button>
              <button
                onClick={() => setAsking((v) => !v)}
                title="Ask this project what the task means, what it's for, and why now — answered by the session itself"
                className={`rounded-md px-3 py-1.5 ${asking ? 'bg-sky-600 hover:bg-sky-500' : 'bg-zinc-700 hover:bg-zinc-600'}`}
              >
                ❓ Ask
              </button>
              <button
                onClick={() => { setEditing(top.id); setEditText(top.task); }}
                className="rounded-md bg-zinc-700 hover:bg-zinc-600 px-3 py-1.5"
              >
                ✏️ Edit
              </button>
              <button
                disabled={busy === top.id}
                onClick={() => decide(top.id, 'skip')}
                title="Not now — dismiss without asking for a replacement; the next sweep comes back to it"
                className="rounded-md bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 px-3 py-1.5"
              >
                ⏭️ Skip
              </button>
              <button
                disabled={busy === top.id}
                onClick={() => decide(top.id, 'reject')}
                title="Reject — ask the session for a different task right away"
                className="rounded-md bg-red-700/80 hover:bg-red-600 disabled:opacity-50 px-3 py-1.5"
              >
                ❌ Reject
              </button>
            </>
          )}
        </div>
      </div>
      {/* resize grip — bottom-right corner */}
      <div
        data-conveyor-resize
        {...resizeHandleProps}
        className="absolute bottom-0 right-0 h-4 w-4 border-r-2 border-b-2 border-amber-500/50 hover:border-amber-400 rounded-br-xl"
      />
    </div>
  );
}
